'use client';

import { c as _c } from "react/compiler-runtime";
import { isNsfw, isPolitics, isTroll, type Post } from '@shared/all/domains/posts';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { CheckCircle2, Expand } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import PostItemActions from './PostItemActions';
import PostItemBody from './PostItemBody';
import PostItemHead from './PostItemHead';
type Props = {
  post: Post;
  isPoliticsParent?: boolean;
  isNsfwParent?: boolean;
  showBranch?: boolean;
  isFullLength: boolean;
  disableLink: boolean;
  spaceSlug?: string;
  currentLevel: number;
  isChild?: boolean;
  uniqueId?: string;
};
const ReplyItem = props => {
  const $ = _c(91);
  const {
    fetchPostReplies
  } = usePostsShared(client, props.uniqueId);
  const router = useRouter();
  const [repliesCollapsed, setRepliesCollapsed] = useState(false);
  const repliesRemaining = props.post.replies_count - (props.post.replies?.length || 0);
  let t0;
  if ($[0] !== props.isNsfwParent || $[1] !== props.post.postFactiiisCount) {
    t0 = isNsfw(props.post.postFactiiisCount) || Boolean(props.isNsfwParent);
    $[0] = props.isNsfwParent;
    $[1] = props.post.postFactiiisCount;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [nsfw, setNsfw] = useState(t0);
  let t1;
  if ($[3] !== props.isPoliticsParent || $[4] !== props.post.postFactiiisCount || $[5] !== props.post.space) {
    t1 = isPolitics(props.post.postFactiiisCount, props.post.space) || Boolean(props.isPoliticsParent);
    $[3] = props.isPoliticsParent;
    $[4] = props.post.postFactiiisCount;
    $[5] = props.post.space;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const [politics, setPolitics] = useState(t1);
  let t2;
  if ($[7] !== props.post.postFactiiisCount) {
    t2 = isTroll(props.post.postFactiiisCount);
    $[7] = props.post.postFactiiisCount;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const [troll, setTroll] = useState(t2);
  const mobile = typeof window !== "undefined" && window.innerWidth <= 600;
  let t3;
  if ($[9] !== props.post.id) {
    t3 = hovered => {
      const branches = document.querySelectorAll(`[data-branch-id="${props.post.id}"]`);
      branches.forEach(branch => branch.classList[hovered ? "add" : "remove"]("border-ds-border-hover"));
    };
    $[9] = props.post.id;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleHover = t3;
  let t4;
  if ($[11] !== handleHover || $[12] !== repliesCollapsed) {
    t4 = () => {
      if (repliesCollapsed) {
        handleHover(false);
      }
    };
    $[11] = handleHover;
    $[12] = repliesCollapsed;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== repliesCollapsed) {
    t5 = [repliesCollapsed];
    $[14] = repliesCollapsed;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  useEffect(t4, t5);
  const t6 = `reply-${props.post.id}`;
  const t7 = `${props.isChild ? "col-span-2 col-start-2" : ""} relative grid grid-cols-[40px_40px_1fr] gap-x-1 md:grid-cols-[50px_50px_1fr]`;
  const t8 = props.post.triggeredByFactiii ? `/f/${props.post.triggeredByFactiii.slug}` : `/user/${props.post.user.username}`;
  let t9;
  if ($[16] !== props.post.triggeredByFactiii || $[17] !== props.post.user) {
    t9 = props.post.triggeredByFactiii ? <Avatar factiiiAvatarKey={props.post.triggeredByFactiii.avatarKey} factiiiId={props.post.triggeredByFactiii.id} size={35} className="relative z-20 mt-2 max-h-[35px] max-w-[35px] object-cover object-top" /> : <Avatar robohash={props.post.user.robohash} avatarKey={props.post.user.avatarKey} isBot={props.post.user.tag === "BOT"} size={35} className="relative z-20 mt-2 max-h-[35px] max-w-[35px] rounded-full object-cover object-top" userId={props.post.user.id} isAnonymous={props.post.user.anonymous} />;
    $[16] = props.post.triggeredByFactiii;
    $[17] = props.post.user;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <Link onClick={_temp} href={t8}>{t9}</Link>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleHover || $[23] !== props.post.id || $[24] !== props.post.replies_count || $[25] !== repliesCollapsed) {
    t11 = props.post.replies_count > 0 && <div className="mt-2 h-full cursor-pointer border-2 border-ds-border-subtle" data-branch-id={props.post.id} onClick={() => setRepliesCollapsed(!repliesCollapsed)} onMouseEnter={() => handleHover(true)} onMouseLeave={() => handleHover(false)} />;
    $[22] = handleHover;
    $[23] = props.post.id;
    $[24] = props.post.replies_count;
    $[25] = repliesCollapsed;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== repliesCollapsed) {
    t12 = repliesCollapsed && <div className="right-5 mb-2 cursor-pointer rounded-full border-2 border-ds-border-subtle bg-transparent p-2" onClick={() => setRepliesCollapsed(false)}><Expand height={16} width={16} /></div>;
    $[27] = repliesCollapsed;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t12) {
    t13 = <div className="col-start-1 flex flex-col items-center">{t10}{t11}{t12}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== props.post.id || $[34] !== props.post.isPrivate || $[35] !== props.post.slug || $[36] !== router) {
    t14 = e_0 => {
      if (!(e_0.target as HTMLElement).closest("#inputId")) {
        const postUrl = props.post.isPrivate ? `/private-post/${props.post.id}` : props.post.slug ? `/post/${props.post.slug}` : `/post/${props.post.id}`;
        router.push(postUrl);
      }
    };
    $[33] = props.post.id;
    $[34] = props.post.isPrivate;
    $[35] = props.post.slug;
    $[36] = router;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== props.post.isTopAnswer) {
    t15 = props.post.isTopAnswer && <div className="mb-1 flex items-center gap-1.5 text-[11px] font-emphasis text-green-500"><CheckCircle2 size={13} />Top answer</div>;
    $[38] = props.post.isTopAnswer;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== nsfw || $[41] !== politics || $[42] !== props.post || $[43] !== props.spaceSlug || $[44] !== props.uniqueId || $[45] !== troll) {
    t16 = <PostItemHead spaceSlug={props.spaceSlug} post={props.post} nsfw={nsfw} politics={politics} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} uniqueId={props.uniqueId} isReply={true} />;
    $[40] = nsfw;
    $[41] = politics;
    $[42] = props.post;
    $[43] = props.spaceSlug;
    $[44] = props.uniqueId;
    $[45] = troll;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = [];
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== nsfw || $[49] !== politics || $[50] !== props.disableLink || $[51] !== props.isFullLength || $[52] !== props.post || $[53] !== troll) {
    t18 = <PostItemBody isFullLength={props.isFullLength} post={props.post} politics={politics} nsfw={nsfw} disableLink={props.disableLink} troll={troll} isInView={true} ignorePostFactiiiIds={t17} />;
    $[48] = nsfw;
    $[49] = politics;
    $[50] = props.disableLink;
    $[51] = props.isFullLength;
    $[52] = props.post;
    $[53] = troll;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t15 || $[56] !== t16 || $[57] !== t18) {
    t19 = <div className="rounded-xl bg-background-secondary px-3 py-2">{t15}{t16}{t18}</div>;
    $[55] = t15;
    $[56] = t16;
    $[57] = t18;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  const t20 = props.currentLevel > (mobile ? 1 : 4);
  let t21;
  if ($[59] !== nsfw || $[60] !== politics || $[61] !== props.post || $[62] !== props.uniqueId || $[63] !== t20 || $[64] !== troll) {
    t21 = <div className="pt-1"><PostItemActions post={props.post} nsfw={nsfw} politics={politics} troll={troll} setNsfw={setNsfw} setPolitics={setPolitics} setTroll={setTroll} isReply={true} redirectOnReply={t20} uniqueId={props.uniqueId} /></div>;
    $[59] = nsfw;
    $[60] = politics;
    $[61] = props.post;
    $[62] = props.uniqueId;
    $[63] = t20;
    $[64] = troll;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== t14 || $[67] !== t19 || $[68] !== t21) {
    t22 = <div className="col-span-2 col-start-2 py-2" onClick={t14}>{t19}{t21}</div>;
    $[66] = t14;
    $[67] = t19;
    $[68] = t21;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  let t23;
  if ($[70] !== handleHover || $[71] !== nsfw || $[72] !== politics || $[73] !== props || $[74] !== repliesCollapsed) {
    t23 = !repliesCollapsed && props.post.replies?.map((reply, i) => <React.Fragment key={`${reply.id}-${i}`}><div className="relative col-start-1 flex justify-center"><div className="absolute mt-2 size-10 -translate-y-1/2 translate-x-[45%] cursor-pointer rounded-bl-2xl border-b-4 border-l-4 border-ds-border-subtle" data-branch-id={props.post.id} onMouseEnter={() => handleHover(true)} onMouseLeave={() => handleHover(false)} onClick={() => setRepliesCollapsed(!repliesCollapsed)} />{i < props.post.replies_count - 1 && <div className="h-full cursor-pointer border-2 border-ds-border-subtle" data-branch-id={props.post.id} onClick={() => setRepliesCollapsed(!repliesCollapsed)} onMouseEnter={() => handleHover(true)} onMouseLeave={() => handleHover(false)} />}</div><ReplyItem {...props} post={reply} isChild={true} isPoliticsParent={politics} isNsfwParent={nsfw} currentLevel={props.currentLevel + 1} showBranch={i !== props.post.replies_count - 1} isFullLength={false} /></React.Fragment>);
    $[70] = handleHover;
    $[71] = nsfw;
    $[72] = politics;
    $[73] = props;
    $[74] = repliesCollapsed;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] !== fetchPostReplies || $[77] !== props.currentLevel || $[78] !== props.post.id || $[79] !== props.post.isPrivate || $[80] !== props.post.replies || $[81] !== repliesCollapsed || $[82] !== repliesRemaining) {
    t24 = !repliesCollapsed && props.post.replies && repliesRemaining > 0 && <><div className="col-start-1 flex justify-end"><div className="mr-[1.5px] size-1/2 rounded-bl-3xl border-b-4 border-l-4 border-ds-border-subtle" data-branch-id={props.post.id} /></div>{props.currentLevel < (mobile ? 1 : 4) ? <button onClick={() => fetchPostReplies(props.post.id)} type="button" className="col-span-2 col-start-2 -mt-2 text-left text-textSecondary/50">Load {repliesRemaining} More{" "}{repliesRemaining === 1 ? "Reply" : "Replies"}</button> : <Link href={props.post.isPrivate ? `/private-post/${props.post.id}` : `/post/${props.post.id}`} className="col-span-2 col-start-2 -mt-2 text-left text-textSecondary/50"><p className="text-textSecondary/50">View More Replies</p></Link>}</>;
    $[76] = fetchPostReplies;
    $[77] = props.currentLevel;
    $[78] = props.post.id;
    $[79] = props.post.isPrivate;
    $[80] = props.post.replies;
    $[81] = repliesCollapsed;
    $[82] = repliesRemaining;
    $[83] = t24;
  } else {
    t24 = $[83];
  }
  let t25;
  if ($[84] !== t13 || $[85] !== t22 || $[86] !== t23 || $[87] !== t24 || $[88] !== t6 || $[89] !== t7) {
    t25 = <div id={t6} className={t7}>{t13}{t22}{t23}{t24}</div>;
    $[84] = t13;
    $[85] = t22;
    $[86] = t23;
    $[87] = t24;
    $[88] = t6;
    $[89] = t7;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  return t25;
};
export default ReplyItem;
function _temp(e) {
  return e.stopPropagation();
}